import { useParams, Link, Navigate } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { ArrowLeft, BookOpen, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface BlogArticle {
  slug: string;
  title: string;
  summary: string;
  tag: string;
  date: string;
  content: string[];
}

export const blogArticles: BlogArticle[] = [
  {
    slug: "dependencia-quimica-sinais-causas-ajuda",
    title: "Dependência Química: Sinais, Causas e Como Buscar Ajuda",
    summary: "Entenda os principais sinais de dependência química, as causas mais comuns e como dar o primeiro passo para buscar tratamento profissional.",
    tag: "Dependência Química",
    date: "2026-03-15",
    content: [
      "A dependência química é uma doença crônica que afeta milhões de brasileiros. Caracteriza-se pela busca compulsiva por substâncias psicoativas, apesar das consequências negativas para a saúde física, mental e social do indivíduo.",
      "Entre os principais sinais de alerta estão: mudanças bruscas de comportamento, isolamento social, perda de interesse em atividades antes prazerosas, alterações no sono e apetite, problemas financeiros recorrentes, mentiras frequentes e deterioração da higiene pessoal.",
      "As causas da dependência química são multifatoriais, envolvendo predisposição genética, fatores ambientais, traumas emocionais, pressão social e condições de saúde mental preexistentes como ansiedade e depressão.",
      "O primeiro passo para buscar ajuda é reconhecer o problema. Conversar com um profissional de saúde, como um psiquiatra ou psicólogo, é fundamental para avaliar a situação e definir o melhor plano de tratamento.",
      "As opções de tratamento incluem acompanhamento ambulatorial, internação em clínicas de reabilitação, grupos de apoio e terapia medicamentosa. O tratamento deve ser individualizado e considerar as necessidades específicas de cada paciente.",
      "Se você ou alguém que você conhece está enfrentando problemas com dependência química, não hesite em procurar ajuda profissional. A recuperação é possível e milhares de pessoas conseguem retomar suas vidas após o tratamento adequado.",
    ],
  },
  {
    slug: "internacao-voluntaria-involuntaria-diferencas",
    title: "Internação Voluntária vs. Involuntária: Entenda as Diferenças",
    summary: "Saiba quais são os tipos de internação previstos em lei, quando cada uma é indicada e como funciona o processo para familiares.",
    tag: "Tratamento",
    date: "2026-03-10",
    content: [
      "A Lei nº 10.216/2001, conhecida como Lei da Reforma Psiquiátrica, estabelece três tipos de internação psiquiátrica no Brasil: voluntária, involuntária e compulsória. Cada modalidade possui regras e procedimentos específicos.",
      "A internação voluntária ocorre quando o próprio paciente consente com o tratamento. É a forma mais recomendada, pois a motivação do paciente contribui significativamente para o sucesso da recuperação. O paciente pode solicitar sua alta a qualquer momento.",
      "A internação involuntária é solicitada por um familiar ou responsável legal, sem o consentimento do paciente. É indicada quando o dependente apresenta risco para si mesmo ou para terceiros. Requer laudo médico circunstanciado e deve ser comunicada ao Ministério Público em até 72 horas.",
      "A internação compulsória é determinada por ordem judicial, mediante laudo médico. É utilizada em situações extremas, quando todas as outras alternativas foram esgotadas e há risco iminente à vida do paciente ou de outros.",
      "Independentemente do tipo, toda internação deve ocorrer em estabelecimento devidamente credenciado, com equipe multidisciplinar qualificada. O tratamento deve respeitar a dignidade do paciente e seus direitos fundamentais.",
      "Para familiares que estão considerando a internação de um ente querido, é essencial buscar orientação jurídica e médica. O processo pode ser emocionalmente difícil, mas em muitos casos é a decisão que salva vidas.",
    ],
  },
  {
    slug: "papel-da-familia-na-recuperacao",
    title: "O Papel da Família na Recuperação do Dependente",
    summary: "A participação familiar é essencial para o sucesso do tratamento. Descubra como apoiar sem reforçar comportamentos nocivos.",
    tag: "Família",
    date: "2026-03-05",
    content: [
      "A família desempenha um papel central no processo de recuperação de um dependente químico. Estudos mostram que pacientes com suporte familiar ativo têm taxas significativamente maiores de sucesso no tratamento.",
      "No entanto, é importante que a família entenda a diferença entre apoiar e ser codependente. A codependência ocorre quando familiares, mesmo com boas intenções, acabam facilitando o comportamento destrutivo do dependente.",
      "Estabelecer limites saudáveis é fundamental. Isso significa não encobrir consequências do uso de substâncias, não fornecer dinheiro que será usado para comprar drogas e não aceitar comportamentos abusivos sob justificativa da dependência.",
      "A terapia familiar é uma ferramenta poderosa. Nela, todos os membros aprendem sobre a doença, desenvolvem habilidades de comunicação e trabalham juntos para criar um ambiente favorável à recuperação.",
      "Grupos de apoio para familiares, como o Nar-Anon e o Al-Anon, oferecem um espaço seguro para compartilhar experiências, aprender com outros que vivem situações semelhantes e encontrar suporte emocional.",
      "Lembre-se: cuidar de si mesmo não é egoísmo. Familiares que mantêm sua própria saúde mental e física estão em melhor posição para ajudar seu ente querido no caminho da recuperação.",
    ],
  },
  {
    slug: "saude-mental-e-dependencia-tratamento-integrado",
    title: "Saúde Mental e Dependência: Por que Tratar Tudo ao Mesmo Tempo",
    summary: "Transtornos psiquiátricos e dependência frequentemente coexistem. O tratamento integrado oferece melhores resultados a longo prazo.",
    tag: "Saúde Mental",
    date: "2026-02-28",
    content: [
      "A coocorrência de transtornos mentais e dependência química, conhecida como diagnóstico dual ou comorbidade, é mais comum do que se imagina. Estima-se que cerca de 50% dos dependentes químicos apresentam algum transtorno psiquiátrico associado.",
      "Os transtornos mais frequentemente associados à dependência incluem depressão, ansiedade, transtorno bipolar, TEPT (transtorno de estresse pós-traumático) e transtornos de personalidade.",
      "Tratar apenas a dependência sem abordar o transtorno mental subjacente (ou vice-versa) reduz significativamente as chances de recuperação. Muitas recaídas ocorrem justamente porque a condição psiquiátrica não foi adequadamente tratada.",
      "O tratamento integrado combina abordagens psiquiátricas e de dependência química simultaneamente. Isso inclui medicação adequada, psicoterapia, terapia ocupacional e grupos terapêuticos, todos conduzidos por uma equipe multidisciplinar.",
      "Ao escolher uma clínica de reabilitação, verifique se ela oferece avaliação psiquiátrica completa e tratamento para comorbidades. Clínicas com psiquiatras, psicólogos e terapeutas especializados oferecem um cuidado mais abrangente.",
      "A recuperação é um processo que exige paciência e persistência. Com o tratamento adequado, é possível estabilizar tanto a saúde mental quanto manter a abstinência, construindo uma vida plena e saudável.",
    ],
  },
  {
    slug: "como-escolher-clinica-reabilitacao-ideal",
    title: "Como Escolher a Clínica de Reabilitação Ideal",
    summary: "Dicas práticas para avaliar estrutura, equipe, localização e custo-benefício na hora de escolher uma clínica de recuperação.",
    tag: "Guia Prático",
    date: "2026-02-20",
    content: [
      "Escolher a clínica de reabilitação certa é uma decisão crucial que pode determinar o sucesso do tratamento. Com centenas de opções disponíveis, é importante saber o que avaliar antes de tomar uma decisão.",
      "A equipe profissional é o fator mais importante. Verifique se a clínica conta com médico psiquiatra, clínico geral, psicólogos, terapeutas ocupacionais, assistentes sociais e equipe de enfermagem 24 horas. O diretor técnico deve ser um médico com CRM ativo.",
      "A estrutura física deve oferecer um ambiente seguro, limpo e acolhedor. Verifique as acomodações, áreas de convivência, espaços para atividades terapêuticas e recreativas, e condições de higiene e segurança.",
      "O programa terapêutico deve ser individualizado. Desconfie de clínicas que oferecem um único modelo de tratamento para todos. Cada paciente possui necessidades específicas que devem ser consideradas no plano terapêutico.",
      "Considere a localização. Alguns pacientes se beneficiam de clínicas próximas à família para facilitar visitas, enquanto outros precisam de um ambiente distante do contexto de uso. Avalie o que é melhor para cada caso.",
      "Compare valores e formas de pagamento, mas não faça da questão financeira o único critério. O mais barato nem sempre é o mais adequado, assim como o mais caro não garante o melhor resultado. Busque o melhor custo-benefício. Utilize o Busca Clínicas para comparar clínicas e encontrar a melhor opção.",
    ],
  },
  {
    slug: "prevencao-de-recaidas-estrategias",
    title: "Prevenção de Recaídas: Estratégias que Funcionam",
    summary: "Conheça as principais estratégias de prevenção de recaídas utilizadas em clínicas de reabilitação, incluindo acompanhamento pós-alta.",
    tag: "Recuperação",
    date: "2026-02-15",
    content: [
      "A recaída é uma realidade no processo de recuperação da dependência química. Estudos indicam que entre 40% e 60% dos pacientes passam por pelo menos uma recaída. No entanto, isso não significa fracasso — é parte do processo de aprendizado.",
      "A prevenção de recaídas começa durante o tratamento. O paciente aprende a identificar situações de risco, gatilhos emocionais e comportamentais que podem levar ao uso de substâncias.",
      "Desenvolver um plano de prevenção personalizado é essencial. Esse plano inclui estratégias de enfrentamento, rede de apoio, atividades alternativas e um protocolo de ação caso o desejo de usar se manifeste.",
      "O acompanhamento pós-alta é tão importante quanto o tratamento inicial. Consultas regulares com psiquiatra e psicólogo, participação em grupos de apoio e manutenção de atividades terapêuticas reduzem significativamente o risco de recaída.",
      "Mudanças no estilo de vida são fundamentais: praticar exercícios físicos regularmente, manter uma alimentação saudável, cultivar hobbies, construir novos vínculos sociais e evitar ambientes e pessoas associados ao uso de substâncias.",
      "Se uma recaída acontecer, é importante não desistir. Busque ajuda imediatamente, reavalie o plano de tratamento e considere se ajustes são necessários. Cada tentativa de recuperação fortalece as habilidades de enfrentamento e aumenta as chances de sucesso a longo prazo.",
    ],
  },
];

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const article = blogArticles.find((a) => a.slug === slug);

  useDocumentMeta({
    title: article ? `${article.title} | Busca Clínicas` : "Artigo não encontrado",
    description: article?.summary || "",
    canonical: article ? `https://buscaclinicas.com.br/blog/${article.slug}` : undefined,
    ogType: "article",
  });

  if (!article) return <Navigate to="/" replace />;

  const formattedDate = new Date(article.date + "T12:00:00").toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    datePublished: article.date,
    publisher: { "@type": "Organization", name: "Busca Clínicas" },
  };

  return (
    <main className="min-h-screen bg-background">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Button variant="ghost" size="sm" className="mb-6 gap-1" asChild>
          <Link to="/"><ArrowLeft className="h-4 w-4" /> Voltar</Link>
        </Button>

        <span className="text-[11px] font-semibold uppercase tracking-wider text-primary bg-primary/10 px-2.5 py-1 rounded-full">
          {article.tag}
        </span>

        <h1 className="text-2xl sm:text-3xl font-bold text-foreground mt-4 mb-2 leading-tight">
          {article.title}
        </h1>

        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-8">
          <Calendar className="h-3.5 w-3.5" />
          <time dateTime={article.date}>{formattedDate}</time>
        </div>

        <div className="space-y-4 text-muted-foreground leading-relaxed">
          {article.content.map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" /> Outros artigos
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {blogArticles
              .filter((a) => a.slug !== slug)
              .slice(0, 4)
              .map((a) => (
                <Link
                  key={a.slug}
                  to={`/blog/${a.slug}`}
                  className="block p-3 rounded-lg border border-border/50 hover:bg-accent/50 transition-colors"
                >
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                    {a.tag}
                  </span>
                  <p className="text-sm font-medium text-foreground mt-1 leading-snug">{a.title}</p>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </main>
  );
}
